import arrayMutators from "final-form-arrays";
import React, { useState } from "react";
import { Form } from "react-final-form";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  LinearProgress,
  Typography,
  Fade,
  useTheme,
  useMediaQuery,
  stepConnectorClasses,
  styled,
} from "@mui/material";
import {
  WestOutlined,
  EastOutlined,
  CheckCircleOutlined,
} from "@mui/icons-material";
import Flx from "../layout/Flx";

import RffFormSpy from "./shared/RffFormSpy";
import RffGroupContainer from "./shared/RffGroupContainer";
import Txt from "../typography/Txt";

// ─── Custom Connector Line ───────────────────────────────────────
const StyledConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 14,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
    borderRadius: 1,
    transition: "background 0.35s ease",
  },
}));

// ─── Custom Step Icon ────────────────────────────────────────────
const StepIconRoot = styled("div")(({ theme, ownerState }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: "50%",
  fontSize: 12,
  fontWeight: 700,
  transition: "all 0.3s ease",
  ...(ownerState.completed && {
    background: theme.palette.primary.main,
    color: "#fff",
  }),
  ...(ownerState.active && {
    background: theme.palette.primary.main,
    color: "#fff",
    boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
  }),
  ...(!ownerState.active &&
    !ownerState.completed && {
      background: theme.palette.action.hover,
      color: theme.palette.text.disabled,
    }),
}));

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>
      {completed ? (
        <CheckCircleOutlined sx={{ fontSize: 18 }} />
      ) : (
        <span>{icon}</span>
      )}
    </StepIconRoot>
  );
}

// ─── Styled Linear Progress ─────────────────────────────────────
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: theme.palette.divider,
  "& .MuiLinearProgress-bar": {
    borderRadius: 2,
    transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
  },
}));

/**
 * A compact form stepper with optional linear progress bar.
 *
 * Props:
 * - initialValues: object with initial form values
 * - onSubmit: function to call with form values on final submit
 * - children: one or more step components (<YourStepComponent stepName="..." validate={...} />)
 * - loading: boolean to disable navigation during async operations
 * - sx: style overrides for the root container
 * - progressVariant: 'stepper' (default) to show step labels, or 'linear' to show a LinearProgress bar
 */
const RffFormStepper = ({
  initialValues,
  onSubmit,
  children,
  loading = false,
  sx,
  alternativeLabel = true,
  progressVariant = "stepper",
  submitText = "Submit",
  stepperSx,
  contentSx,
  formSpy = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const steps = React.Children.toArray(children).filter(Boolean);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const isLastStep = step === steps.length - 1;

  const showStepper = progressVariant === "stepper" && !isMobile;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={steps[step].props.validate}
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, form, submitting }) => {
        const runValidation = () => {
          form.getRegisteredFields().forEach((name) => form.blur(name));
          const errors = form.getState().errors || {};
          return Object.keys(errors).length === 0;
        };

        const handleNext = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (runValidation()) {
            setDirection("forward");
            setStep((s) => s + 1);
          }
        };

        const handleBack = () => {
          setDirection("back");
          setStep((s) => s - 1);
        };

        const handleFormSubmit = (e) => {
          if (!isLastStep) {
            e.preventDefault();
            return;
          }
          handleSubmit(e);
        };

        const linearValue =
          steps.length > 0 ? ((step + 1) / steps.length) * 100 : 0;

        return (
          <Box
            component="form"
            onSubmit={handleFormSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              ...sx,
            }}
          >
            {/* ── Progress Indicator ── */}
            {showStepper ? (
              <Stepper
                activeStep={step}
                alternativeLabel={alternativeLabel}
                connector={<StyledConnector />}
                sx={{
                  mb: alternativeLabel ? 4 : 2,
                  ...stepperSx,
                }}
              >
                {steps.map((child, idx) => (
                  <Step key={idx}>
                    <StepLabel
                      StepIconComponent={CustomStepIcon}
                      sx={{
                        "& .MuiStepLabel-label": {
                          mt: alternativeLabel ? "8px !important" : 0,
                          fontSize: 11,
                          fontWeight: idx <= step ? 600 : 400,
                          color: idx <= step ? "text.primary" : "text.disabled",
                          letterSpacing: 0.2,
                          lineHeight: 1.2,
                          // fontFamily: "Inter, sans-serif",
                          transition: "all 0.25s ease",
                        },
                      }}
                    >
                      {child.props.stepName ||
                        child.props.title ||
                        `Step ${idx + 1}`}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <Box sx={{ mb: 4, ...stepperSx }}>
                {/* Mobile: linear progress + step label */}
                <Flx jb ac sx={{ mb: 1 }}>
                  <Txt
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      letterSpacing: 0.3,
                    }}
                  >
                    {steps[step].props.stepName ||
                      steps[step].props.title ||
                      `Step ${step + 1}`}
                  </Txt>
                  <Txt variant="caption" sx={{ color: "text.disabled" }}>
                    {step + 1} / {steps.length}
                  </Txt>
                </Flx>
                <StyledLinearProgress
                  variant="determinate"
                  value={linearValue}
                />
              </Box>
            )}

            {/* ── Step Content with Fade ── */}
            <Fade in key={step} timeout={250}>
              <Box sx={contentSx}>
                <RffGroupContainer flexDirection="column">
                  {steps[step]}
                </RffGroupContainer>
              </Box>
            </Fade>

            {/* ── Navigation ── */}
            <Flx
              fw
              jb
              ac
              wrap
              sx={{
                mt: 4,
                pt: 2.5,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* Back button — always rendered but invisible when on step 0 */}
              <Button
                size="medium"
                variant="text"
                startIcon={
                  <WestOutlined sx={{ fontSize: "16px !important" }} />
                }
                onClick={handleBack}
                type="button"
                color="inherit"
                disabled={step === 0}
                sx={{
                  opacity: step > 0 ? 1 : 0,
                  pointerEvents: step > 0 ? "auto" : "none",
                  fontWeight: 500,
                  textTransform: "none",
                  color: "text.secondary",
                  transition: "opacity 0.25s ease",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                Back
              </Button>

              <Flx g={1}>
                {/* Skip button (if step is skippable) */}
                {steps[step].props?.skippable && (
                  <Button
                    variant="outlined"
                    size="medium"
                    color="inherit"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDirection("forward");
                      setStep((s) => s + 1);
                    }}
                    type="button"
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor: "divider",
                      color: "text.secondary",
                      "&:hover": {
                        borderColor: "text.disabled",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    Skip
                  </Button>
                )}

                {/* Next / Submit */}
                {!isLastStep ? (
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={handleNext}
                    type="button"
                    endIcon={
                      <EastOutlined sx={{ fontSize: "16px !important" }} />
                    }
                    disableElevation
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      borderRadius: 1.5,
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    type="submit"
                    size="medium"
                    loading={loading}
                    disabled={submitting || loading}
                    disableElevation
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      borderRadius: 1.5,
                    }}
                  >
                    {submitText}
                  </Button>
                )}
              </Flx>
            </Flx>

            <RffFormSpy formSpy={formSpy} />
          </Box>
        );
      }}
    </Form>
  );
};

export default RffFormStepper;
